/**
 * API 凭证。
 *
 * **明文 secret 只出现一次** —— 库里存的是 `sha256(secret)`，
 * 明文只在签发/轮换的那一次响应体里返回。丢了只能轮换，这正是「只存哈希」应有的代价。
 * 所以这里必须把它显眼地展示出来并提醒用户存好，关掉就再也拿不回来了。
 *
 * 轮换是「签发一把继承 mode/scopes/label 的新 key，同时给旧 key 一个宽限期」，
 * 这样商户能不中断收款地完成发版。宽限期 0 = 立刻失效。
 */

import { useState } from 'react';
import { request, type Collection } from '../lib/api.ts';
import { useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge, Button, Card, CopyId, ErrorBox, Field, ModeBadge, Modal, Select, TextInput,
} from '../components/ui.tsx';
import { DataTable, type Column } from '../components/table.tsx';
import { formatDateTime, formatRelative } from '../lib/format.ts';
import type { Credential, Mode, Org } from '../lib/types.ts';

/**
 * API 文档地址。
 *
 * 开发期由 Vite 的 docs 中间件把仓库的 `docs/` 挂在 `/docs`；
 * 生产由反向代理提供同一路径。需要挂到别处时用 `VITE_API_DOCS_URL` 覆盖。
 */
const API_DOCS_URL = import.meta.env['VITE_API_DOCS_URL'] ?? '/docs/api.html';

/** 可授予的权限点，与网关的 SCOPES 一一对应。 */
const SCOPES: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'payments:write', label: '发起支付 / 请款 / 取消' },
  { value: 'payments:read', label: '查询支付' },
  { value: 'refunds:write', label: '发起退款' },
  { value: 'refunds:read', label: '查询退款' },
  { value: 'customers:write', label: '维护客户档案' },
  { value: 'customers:read', label: '查询客户档案' },
  { value: 'payment_methods:write', label: '绑卡 / 用保存的卡扣款' },
  { value: 'payment_methods:read', label: '查询已保存的支付方式' },
  { value: 'events:read', label: '查询事件' },
  { value: 'events:write', label: '重投事件（会真的再打一次商户端点）' },
  { value: 'disputes:read', label: '查询争议' },
  { value: 'balance:read', label: '查询余额' },
];

const DEFAULT_SCOPES = ['payments:write', 'payments:read', 'refunds:write', 'refunds:read'];

export function CredentialsPage() {
  const session = useSession();
  const [orgId, setOrgId] = useState(session.me.org.id);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<Credential | null>(null);
  const [rotating, setRotating] = useState<Credential | null>(null);

  const orgs = useAsync<{ data: readonly Org[] }>('/v1/orgs', { query: { limit: 200 } });
  const list = useAsync<Collection<Credential>>(`/v1/orgs/${orgId}/credentials`, {}, [orgId]);

  const revoke = useMutation(async (credentialId: string) =>
    await request(`/v1/orgs/${orgId}/credentials/${credentialId}/revoke`, { method: 'POST' }));

  const columns: readonly Column<Credential>[] = [
    { key: 'key', header: 'Key ID', render: (row) => (
      <div className="stack tight">
        <CopyId id={row.key_id} full />
        {row.label !== null && <span className="cell-muted" style={{ fontSize: 11 }}>{row.label}</span>}
      </div>
    ) },
    { key: 'mode', header: '模式', render: (row) => <ModeBadge mode={row.mode} /> },
    { key: 'status', header: '状态', render: (row) => (
      <Badge tone={
        row.effective_status === 'active' ? 'good'
          : row.effective_status === 'expiring' ? 'warn' : 'bad'
      }>
        {row.effective_status === 'active' ? '有效'
          : row.effective_status === 'expiring' ? `宽限至 ${formatDateTime(row.expires_at)}`
          : row.effective_status === 'expired' ? '已过期' : '已吊销'}
      </Badge>
    ) },
    { key: 'scopes', header: '权限', render: (row) => (
      <div className="row tight">
        {row.scopes.includes('*')
          ? <Badge tone="bad">通配 *</Badge>
          : row.scopes.map((scope) => (
              <span key={scope} className="mono" style={{ fontSize: 11 }}>{scope}</span>
            ))}
      </div>
    ) },
    { key: 'used', header: '最后使用', render: (row) => (
      <span className="cell-muted">{formatRelative(row.last_used_at)}</span>
    ) },
    { key: 'actions', header: '', render: (row) => (
      row.effective_status === 'revoked' || row.effective_status === 'expired' ? null : (
        <div className="row tight">
          <Button size="sm" onClick={() => { setRotating(row); }}>轮换</Button>
          <Button
            size="sm"
            variant="danger"
            disabled={revoke.pending}
            onClick={() => {
              void revoke.run(row.id).then((result) => { if (result !== null) list.reload(); });
            }}
          >
            吊销
          </Button>
        </div>
      )
    ) },
  ];

  return (
    <>
      <div className="row">
        <h1>API 凭证</h1>
        <span style={{ flex: 1 }} />
        {/*
          文档链接放在这里而不是导航栏：拿到 key 的下一步必然是查怎么调，
          两件事挨着才顺手。新窗口打开 —— 别把人从控制台里赶出去。
        */}
        <a className="btn" href={API_DOCS_URL} target="_blank" rel="noreferrer">
          API 文档 ↗
        </a>
        <Button variant="primary" onClick={() => { setIssuing(true); }}>签发新凭证</Button>
      </div>

      <ErrorBox error={revoke.error} />

      <div className="filters">
        <Field label="组织">
          <Select
            value={orgId}
            onChange={setOrgId}
            options={(orgs.data?.data ?? []).map((org) => ({
              value: org.id,
              label: org.id === session.me.org.id ? `${org.name}（本组织）` : org.name,
            }))}
          />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.data?.data ?? []}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty="还没有签发任何凭证"
          emptyHint="商户用这把 key 调用网关 API 发起支付。test 与 live 是两套独立的凭证，互相不能访问对方的资源。"
        />
      </Card>

      {issuing && (
        <IssueModal
          orgId={orgId}
          onClose={() => { setIssuing(false); }}
          onIssued={(credential) => { setIssuing(false); setIssued(credential); list.reload(); }}
        />
      )}
      {rotating !== null && (
        <RotateModal
          orgId={orgId}
          credential={rotating}
          onClose={() => { setRotating(null); }}
          onRotated={(credential) => { setRotating(null); setIssued(credential); list.reload(); }}
        />
      )}
      {issued !== null && (
        <SecretModal credential={issued} onClose={() => { setIssued(null); }} />
      )}
    </>
  );
}

function ScopePicker({
  selected, onChange,
}: {
  selected: readonly string[];
  onChange: (next: readonly string[]) => void;
}) {
  return (
    <div className="stack tight">
      <span className="field-label">权限点</span>
      <span className="field-hint">
        只勾用得上的。一把只想查余额的 key 不该拿到发起支付的权限 ——
        通配 <span className="mono">*</span> 会把将来新增的任何接口也一并授出去，只有平台能给。
      </span>
      <div className="stack tight" style={{ maxHeight: 220, overflowY: 'auto' }}>
        {SCOPES.map((scope) => (
          <label key={scope.value} className="checkbox">
            <input
              type="checkbox"
              checked={selected.includes(scope.value)}
              onChange={(event) => {
                onChange(
                  event.target.checked
                    ? [...selected, scope.value]
                    : selected.filter((item) => item !== scope.value),
                );
              }}
            />
            <span className="mono" style={{ fontSize: 11.5 }}>{scope.value}</span>
            <span className="cell-muted" style={{ fontSize: 11.5 }}>{scope.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function IssueModal({
  orgId, onClose, onIssued,
}: {
  orgId: string;
  onClose: () => void;
  onIssued: (credential: Credential) => void;
}) {
  const [mode, setMode] = useState<Mode>('test');
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<readonly string[]>(DEFAULT_SCOPES);

  const issue = useMutation(async () => await request<Credential>(`/v1/orgs/${orgId}/credentials`, {
    method: 'POST',
    body: {
      mode,
      scopes,
      ...(label === '' ? {} : { label }),
    },
  }));

  return (
    <Modal
      title="签发 API 凭证"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={issue.pending || scopes.length === 0}
            onClick={() => {
              void issue.run().then((result) => { if (result !== null) onIssued(result); });
            }}
          >
            {issue.pending ? '签发中…' : '签发'}
          </Button>
        </>
      }
    >
      <ErrorBox error={issue.error} />

      <div className="grid cols-2">
        <Field label="模式" hint="test 凭证访问不到 live 资源，反之亦然。">
          <Select
            value={mode}
            onChange={(value) => { setMode(value as Mode); }}
            options={[{ value: 'test', label: 'test' }, { value: 'live', label: 'live' }]}
          />
        </Field>
        <Field label="备注名" hint="例如「生产后端」「对账脚本」">
          <TextInput value={label} onChange={setLabel} />
        </Field>
      </div>

      {mode === 'live' && (
        <div className="alert" data-tone="bad">
          <span className="alert-title">这是 live 凭证</span>
          <span>持有它的人可以对真实资金发起操作。请只把它配置在受控的服务端环境里。</span>
        </div>
      )}

      <ScopePicker selected={scopes} onChange={setScopes} />
    </Modal>
  );
}

function RotateModal({
  orgId, credential, onClose, onRotated,
}: {
  orgId: string;
  credential: Credential;
  onClose: () => void;
  onRotated: (credential: Credential) => void;
}) {
  const [graceHours, setGraceHours] = useState('24');

  const rotate = useMutation(async () =>
    await request<Credential>(`/v1/orgs/${orgId}/credentials/${credential.id}/rotate`, {
      method: 'POST',
      body: { grace_seconds: Math.round(Number(graceHours) * 3600) },
    }));

  return (
    <Modal
      title="轮换凭证"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={rotate.pending}
            onClick={() => {
              void rotate.run().then((result) => { if (result !== null) onRotated(result); });
            }}
          >
            {rotate.pending ? '轮换中…' : '轮换'}
          </Button>
        </>
      }
    >
      <ErrorBox error={rotate.error} />

      <p className="field-hint">
        将签发一把继承 <span className="mono">{credential.mode}</span> 模式与相同权限点的新 key，
        旧 key <span className="mono">{credential.key_id}</span> 在宽限期后失效。
      </p>

      <Field
        label="旧 key 宽限期（小时）"
        hint="0 = 立刻失效。默认 24 小时，够完成一次发版；上限 30 天 —— 再长就不叫换 key，那是同时维护两把 key。"
      >
        <TextInput value={graceHours} onChange={setGraceHours} numeric />
      </Field>
    </Modal>
  );
}

/**
 * 明文 secret 展示。
 *
 * 这是它**唯一一次**出现的地方 —— 库里只有 sha256，没有任何接口能再查回来。
 * 所以这个弹窗要说得足够清楚，并且默认把整串选中方便复制。
 */
function SecretModal({ credential, onClose }: { credential: Credential; onClose: () => void }) {
  return (
    <Modal
      title="凭证已签发"
      onClose={onClose}
      wide
      footer={<Button variant="primary" onClick={onClose}>我已保存</Button>}
    >
      <div className="alert" data-tone="warn">
        <span className="alert-title">这是 secret 唯一一次出现</span>
        <span>
          系统里只存它的哈希，关掉这个窗口后**再也无法查看**。丢了只能轮换。
          请立刻存进你的密钥管理器。
        </span>
      </div>

      <Field label="Key ID（公开标识，可以放进日志）">
        <div className="secret-box">{credential.key_id}</div>
      </Field>

      <Field label="Secret">
        <div className="secret-box">{credential.secret ?? '（未返回）'}</div>
      </Field>

      <Field label="Bearer Token（直接放进 Authorization 头）">
        <div className="secret-box">{credential.bearer_token ?? '（未返回）'}</div>
      </Field>

      <Field label="调用示例">
        <div className="secret-box">
          {`curl https://api.bipay.example/v1/payments \\
  -H "authorization: Bearer ${credential.bearer_token ?? '<token>'}" \\
  -H "idempotency-key: $(uuidgen)" \\
  -H "content-type: application/json" \\
  -d '{"amount":"1000","currency":"USD","order_no":"ORDER-1"}'`}
        </div>
      </Field>
    </Modal>
  );
}
